import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { db } from '@/services';
import type { RouteDefinition } from '@/types';
import { notifyLiveDataError, todayIsoDate } from '@/utils';
import { createStartupSnapshotLogger, logStartupTiming } from '@/utils/startupPerformance';

/** True if `route` is claimable "today", honoring its optional active date window. */
const isWithinActiveWindow = (route: RouteDefinition, today: string) => {
  if (route.category === 'extra') return true;
  if (route.activeFrom && today < route.activeFrom) return false;
  if (route.activeUntil && today > route.activeUntil) return false;
  return true;
};

/**
 * Subscribes to the live-updating list of active routes, ordered by start
 * time, filtered client-side to only those within their activeFrom/activeUntil
 * window today (the Cloud Functions enforce this too, but filtering here keeps
 * users from seeing routes they can't actually claim yet or anymore).
 */
export const useActiveRoutes = () => {
  const [routes, setRoutes] = useState<RouteDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const logSnapshot = useMemo(() => createStartupSnapshotLogger('active-routes'), []);

  useEffect(() => {
    const startedAt = performance.now();
    const q = query(
      collection(db, 'routes'),
      where('active', '==', true),
      orderBy('startTime', 'asc'),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        logSnapshot({
          fromCache: snapshot.metadata.fromCache,
          resultCount: snapshot.size,
        });
        const today = todayIsoDate();
        setRoutes(
          snapshot.docs
            .map((docSnap) => {
              const route = docSnap.data() as RouteDefinition;
              return {
                ...route,
                id: docSnap.id,
                category: route.category ?? 'pakkeshop',
                extraDurationHours: route.extraDurationHours ?? null,
              };
            })
            .filter((route) => isWithinActiveWindow(route, today)),
        );
        setLoading(false);
      },
      (error) => {
        logStartupTiming('active-routes first-snapshot-error', startedAt, { failed: true });
        notifyLiveDataError('useActiveRoutes', error);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [logSnapshot]);

  return { routes, loading };
};
